/* eslint-disable @altermaind/no-magic-numbers */
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared';
import { ProgressBar } from '@/_shared/components/ProgressBar';
import { RenderLocalesWithComponents } from '@/_shared/components/RenderLocalesWithComponents';
import { Routes } from '@/_shared/constants/routes';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getOrganizationCreditsCall } from '@/organization/state';

export const DivisionByDepartments: FC = () => {
  const t = useTranslations('settings.tabs.credits.department');
  const { genericResponsiveValue } = useResponsiveStyle();
  const navigate = useNavigate();
  const { departments } = getOrganizationCreditsCall.hook();
  const [showMore, setShowMore] = useState(false);

  const gapXs = useThemeValue('space', 'xs');

  const goToDepartment = () => {
    navigate(Routes.Departments);
  };

  const goToTeam = (departmentId: string) => {
    navigate(Routes.DepartmentsDetail.replace(':departmentId', departmentId || ''));
  };

  return (
    <Box mt='s'>
      <Box
        mt='xl'
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: {
            desktop: 'row',
          },
          defaultValue: 'column',
        })}
        alignItems={genericResponsiveValue({
          breakPoints: {
            desktop: 'center',
          },
          defaultValue: 'flex-start',
        })}
        justifyContent='space-between'
      >
        <Typography variant='h3'>{t('title')}</Typography>
        {departments.length > 3 && (
          <Box
            mt={genericResponsiveValue({
              breakPoints: {
                desktop: 'none',
              },
              defaultValue: 'xs',
            })}
            onClick={() => setShowMore(!showMore)}
            style={{ cursor: 'pointer' }}
          >
            {showMore ? (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography variant='p3/light'>{t('showLess')}</Typography>
                <Box style={{ transform: 'rotate(180deg)' }}>
                  <Svg name='down' size='s' fill='grey/light' />
                </Box>
              </Box>
            ) : (
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography variant='p3/light'>{t('showMore')}</Typography>
                <Svg name='down' size='s' fill='grey/light' />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Typography mt='xs' variant='p3/light'>
        {t('description')}
      </Typography>
      <AlertBadge
        type='primary'
        iconName='lightBulb'
        label={
          <Typography variant='p2/light'>
            <RenderLocalesWithComponents
              text={t('alertLabel', {
                downloadFile: '!{LINK}!',
              })}
              placeholders={{
                '!{LINK}!': (
                  <Typography
                    as='span'
                    fontWeight='500'
                    style={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}
                    onClick={goToDepartment}
                  >
                    {t('alertLinkLabel')}
                  </Typography>
                ),
              }}
            />
          </Typography>
        }
        containerProps={{ mt: 's' }}
      />
      <Box
        mt='s'
        style={{ gap: gapXs }}
        display='grid'
        gridTemplateColumns={genericResponsiveValue({
          breakPoints: {
            desktop: 'repeat(3, 1fr)',
            tablet: 'repeat(1, 1fr)',
            mobile: 'repeat(1, 1fr)',
          },
          defaultValue: 'repeat(1, 1fr)',
        })}
      >
        {(showMore ? departments : departments.slice(0, 3)).map(department => (
          <Card key={department.departmentName}>
            <Box
              alignItems='center'
              display='flex'
              flexDirection='row'
              justifyContent='space-between'
              mt='xxxs'
              pb='xs'
            >
              <Typography variant='h3'>{department.departmentName}</Typography>
              <Box alignItems='center' flexDirection='row' display='flex'>
                <Svg name='users' size='s' />
                <Typography ml='xxs' variant='h1'>
                  {department.userNumber}
                </Typography>
              </Box>
            </Box>
            <Box>
              <ProgressBar
                currentPercentage={Number(
                  ((department.averageConsumption / department.assigned) * 100).toFixed(0),
                )}
              />
              <Box display='flex' justifyContent='space-between' mt='xxs'>
                <Box flex='2'>
                  <Typography variant='p2/light'>
                    <RenderLocalesWithComponents
                      text={t('creditPerPerson', {
                        total: '!{TOTAL}!',
                      })}
                      placeholders={{
                        '!{TOTAL}!': (
                          <Typography as='span' variant='p2/light' fontWeight='500'>
                            {department.averageConsumption}
                          </Typography>
                        ),
                      }}
                    />
                  </Typography>
                </Box>
                <Box textAlign='right' flex='1'>
                  <Typography variant='p2/light' fontWeight='500'>
                    {((department.averageConsumption / department.assigned) * 100).toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Button onClick={() => goToTeam(department.departmentId)} variant='positive' mt='m'>
              {t('buttonCta')}
            </Button>
          </Card>
        ))}
      </Box>
    </Box>
  );
};
